import Autolinker from 'autolinker';
import DOMPurify from 'dompurify';

// Preview anchors open outside the preview frame.
function openLinksInNewTab(node: Element): void {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
}

/**
 * Prepares an HTML-authored message body for `dangerouslySetInnerHTML`.
 *
 * Linkify first, sanitize last, so DOMPurify vets the anchors Autolinker adds
 * as well as the markup that arrived in the body.
 *
 * Autolinker's own `sanitizeHtml` option is deliberately not used here: it
 * escapes every `<` and `>`, which renders an email body as literal tags. Only
 * bodies that are plain text by nature can take that option.
 *
 * DOMPurify keeps `style` attributes, and in its default configuration keeps
 * `<style>` elements too. A `<style>` that leads the body is the exception: the
 * parser hoists it into `<head>`, which sanitizing a fragment discards.
 */
export function sanitizeMessageHtml(html?: string | null): string {
  if (!html) {
    return '';
  }

  DOMPurify.addHook('afterSanitizeAttributes', openLinksInNewTab);

  try {
    return DOMPurify.sanitize(Autolinker.link(html));
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', openLinksInNewTab);
  }
}

/**
 * Prepares an untrusted message body that is plain text by nature for
 * `dangerouslySetInnerHTML`.
 *
 * These bodies carry applicant-supplied text, so painting one as markup is a
 * stored XSS sink. Allowing no tags leaves text nodes only, and nothing can
 * execute. The HTML context is deliberate: an outbound body arrives
 * entity-escaped by Rails' `strip_tags`, so a text child would show `&amp;`
 * literally, while an inbound reply's text/plain part skips that branch and
 * arrives raw. Both read correctly through innerHTML.
 *
 * Both policies share the module-level DOMPurify singleton, so the `finally`
 * above is what keeps the anchor hook from running here.
 */
export function stripMessageMarkup(html?: string | null): string {
  if (!html) {
    return '';
  }

  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

export default sanitizeMessageHtml;
